import { API_ENDPOINTS } from '../api/config';
import {
  clearApiCache,
  getDriverStatus,
  type ApiResponse,
  type DriverStatusResponse,
  type DrivingLimitMode,
} from '../api/apiService';
import { syncDrivingLimitNotifications } from './drivingLimitNotifications';
import { storeDrivingLimitSnapshot } from './drivingLimitStatusNotification';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * ALL-174: a save counts only when the server echoes the chosen mode. A server
 * without the feature ignores the unknown field and still answers 200, so
 * trusting `ok` alone would tell the driver a change was saved when it was not.
 */
export const modeSaved = (response: ApiResponse<any>, mode: DrivingLimitMode): boolean =>
  response.ok && response.data?.driving_limit_mode === mode;

interface ResyncDeps {
  clearCache: () => void;
  getStatus: () => Promise<ApiResponse<DriverStatusResponse>>;
  sync: typeof syncDrivingLimitNotifications;
  storeSnapshot: (limit: DriverStatusResponse['driving_limit'] | null) => Promise<void>;
}

const defaultDeps: ResyncDeps = {
  clearCache: () => clearApiCache(`${API_ENDPOINTS.TIMELOGS}driver-status/`),
  getStatus: getDriverStatus,
  sync: syncDrivingLimitNotifications,
  storeSnapshot: (limit) => storeDrivingLimitSnapshot(limit),
};

/**
 * After a mode change the warnings already handed to the OS and the Android
 * countdown still follow the old count. Re-read the status (now in the new
 * mode) and re-sync both, so a driver who switches mid-drive is warned at the
 * right time. On failure nothing changes; the next status poll catches up.
 */
export const resyncDrivingLimitAfterModeChange = async (
  t: Translate,
  language?: string,
  deps: ResyncDeps = defaultDeps,
): Promise<void> => {
  try {
    deps.clearCache();
    const response = await deps.getStatus();
    if (!response.ok || !response.data) return;
    const { driving_limit: limit, is_driving: isDriving } = response.data;
    await deps.sync(limit ?? null, isDriving, t, language);
    await deps.storeSnapshot(isDriving ? limit ?? null : null);
  } catch (error) {
    console.warn('Failed to re-sync driving limit after mode change', error);
  }
};
